// Транспорт экрана «Наборы» (/api/admin/nabory*): владелец правит составы наборов
// разделов и привязку отделов сам, без программиста. Дверь та же, что у учёток, —
// поимённая учётка владельца; 403 здесь означает «не владелец», и это не поломка.
//
// ПРО МОДЕЛЬ, одним абзацем: набор — это «что показывать» (состав меню и стартовой),
// а не «что можно». Права на данные даёт РОЛЬ; набор только пересекается с ней и
// потому умеет лишь прятать. Раздел вне роли не появится, какой бы набор ни назначили.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Unauthorized } from './client'

const БАЗА = '/api/admin/nabory'

/** Один набор: состав и происхождение. */
export interface Набор {
  code: string
  title: string
  /** Коды разделов; null — набор не сужает («видит всё по роли», кодовый «админ»). */
  sections: string[] | null
  /**
   * 'code' — константа rights (правится только перекрытием);
   * 'file' — создан владельцем (можно удалить);
   * 'override' — кодовый, перекрытый файлом (удаление вернёт кодовый состав).
   */
  source: 'code' | 'file' | 'override'
  /** Логины учёток с этим набором — по ним видно, кого правка заденет. */
  users?: string[]
}

/** Раздел, который можно класть в наборы (владельческого и служебного тут нет). */
export interface РазделДляНабора {
  code: string
  title: string
  group: string
}

export interface ОбзорНаборов {
  sets: Набор[]
  sections: РазделДляНабора[]
  /** id отдела (строкой — ключ JSON) → код набора. */
  dept_sets: Record<string, string>
  /** Отделы из ядра (/refs) с названиями. Ядро молчит — поля может не быть. */
  depts?: { id: number; name: string }[]
  /**
   * Учётки, чей набор не существует (например, набор удалили руками в файле).
   * Набора нет = человек видит всё по роли — экран говорит это предупреждением.
   */
  unknown_sets?: { username: string; set: string }[]
}

async function запрос<T>(путь: string, init?: RequestInit): Promise<T> {
  const res = await fetch(БАЗА + путь, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Учётки и доступ» выдаётся не по роли, а по учётке '
        + 'владельца — как касса. Вам он не открыт.')
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

const КЛЮЧ = ['admin-nabory'] as const

export function useНаборы() {
  return useQuery<ОбзорНаборов>({
    queryKey: КЛЮЧ,
    queryFn: () => запрос<ОбзорНаборов>(''),
    staleTime: 10_000,
    retry: 1,
  })
}

/** После успеха перечитать и наборы, и учётки: выпадашка набора у учётки — оттуда. */
function useДействие<Т, Р>(ф: (арг: Т) => Promise<Р>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ф,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: КЛЮЧ })
      void qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

export function useСохранениеНабора() {
  /** sections: null — набор «не сужать» (видит всё по роли), как кодовый «админ». */
  return useДействие((арг: { code: string; title: string; sections: string[] | null }) =>
    запрос<{ ok: boolean }>('/save', { method: 'POST', body: JSON.stringify(арг) }))
}

export function useУдалениеНабора() {
  return useДействие((арг: { code: string }) =>
    запрос<{ ok: boolean; reverted: boolean }>('/save', {
      method: 'POST',
      body: JSON.stringify({ ...арг, delete: true }),
    }))
}

export function useПривязкаОтдела() {
  return useДействие((арг: { dept_id: number; set: string }) =>
    запрос<{ ok: boolean }>('/dept', { method: 'POST', body: JSON.stringify(арг) }))
}
